import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { isNil, omitBy } from 'lodash';

import { RestBuilder } from '../helpers/rest.builder';
import {
  IMobileApp,
  IMobileAppSession,
  IMobileAppCommandRequest,
  IMobileAppCommandResponse,
  IMobileAppScanResponse,
  IMobileScanSaveRequest,
  IMobileCreateSessionRequest,
  IMobileGetDevicesQuery,
  IMobileScanRequest,
  IMobileDeviceResponse,
  IMobileGenerateSessionTokenResponse,
  MobileAppSessionCloseReason,
} from '../../../../shared/interfaces/mobile-app.interface';
import { ApiHeaderOption } from '../../../../shared/constants/api';

@Injectable({
  providedIn: 'root',
})
export class MobileAppTestingApi {
  private restBuilder: RestBuilder;

  constructor(private httpClient: HttpClient) {
    this.restBuilder = new RestBuilder();
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps
  public getMobileApps(workspaceId: string, digitalPropertyId: string): Observable<IMobileApp[]> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps()
      .getApiUrl();

    return this.httpClient.get<IMobileApp[]>(url, {
      headers: {
        [ApiHeaderOption.skipLoader]: 'true',
      },
    });
  }

  // POST /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps
  public uploadMobileApp(workspaceId: string, digitalPropertyId: string, formData: FormData): Observable<IMobileApp> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps()
      .getApiUrl();
    return this.httpClient.post<IMobileApp>(url, formData);
  }

  // PUT /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/:mobileAppsId
  public replaceMobileApp(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    formData: FormData,
  ): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps(mobileAppId)
      .getApiUrl();
    return this.httpClient.put<any>(url, formData);
  }

  // DELETE /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/:mobileAppsId
  public deleteMobileApp(workspaceId: string, digitalPropertyId: string, mobileAppId: string): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps(mobileAppId)
      .getApiUrl();
    return this.httpClient.delete<void>(url);
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/:mobileAppId/sessions/:sessionId
  public getSession(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    sessionId: string,
  ): Observable<IMobileAppSession> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps(mobileAppId)
      .sessions(sessionId)
      .getApiUrl();
    return this.httpClient.get<IMobileAppSession>(url, {
      headers: {
        [ApiHeaderOption.skipLoader]: 'true',
      },
    });
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/sessions
  public getSessionInProgress(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppIds: string[],
    useLoadingIndicator: boolean,
  ): Observable<IMobileAppSession> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps()
      .sessions()
      .getApiUrl();

    const httpParams: HttpParams = new HttpParams({
      fromObject: {
        mobileAppIds,
      },
    });

    return this.httpClient.get<IMobileAppSession>(
      url,
      useLoadingIndicator
        ? { params: httpParams }
        : {
            params: httpParams,
            headers: {
              [ApiHeaderOption.skipLoader]: 'true',
            },
          },
    );
  }

  // POST /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/:mobileAppId/sessions
  public startTestingMobileApp(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    mobileAppCreateSessionRequest: IMobileCreateSessionRequest,
  ): Observable<IMobileAppSession> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps(mobileAppId)
      .sessions()
      .getApiUrl();
    return this.httpClient.post<IMobileAppSession>(url, mobileAppCreateSessionRequest, {
      headers: {
        [ApiHeaderOption.skipLoader]: 'true',
      },
    });
  }

  // DELETE /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/:mobileAppId/sessions/:sessionId
  public endTestingMobileApp(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    mobileAppSessionId: string,
    closeReason?: MobileAppSessionCloseReason,
  ): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps(mobileAppId)
      .sessions(mobileAppSessionId)
      .getApiUrl();
    return this.httpClient.delete<any>(url, { params: closeReason ? { reason: closeReason } : {} });
  }

  // POST /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/:mobileAppId/sessions/:sessionId/scan
  public saveTestingMobileApp(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppId: string,
    mobileAppSessionId: string,
    scanResults: IMobileScanSaveRequest,
  ): Observable<void> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps(mobileAppId)
      .sessions(mobileAppSessionId)
      .scan()
      .getApiUrl();
    return this.httpClient.post<void>(url, scanResults);
  }

  // GET /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-apps/devices
  public getMobileDevices(
    workspaceId: string,
    digitalPropertyId: string,
    params: IMobileGetDevicesQuery,
  ): Observable<IMobileDeviceResponse> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileApps()
      .devices()
      .getApiUrl();

    const httpParams: HttpParams = new HttpParams({
      fromObject: omitBy(params, isNil),
    });

    return this.httpClient.get<IMobileDeviceResponse>(url, {
      params: httpParams,
      headers: { [ApiHeaderOption.skipLoader]: 'true' },
    });
  }

  // PUT  /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-app-sessions/:sessionId/command
  public sendCommand(
    workspaceId: string,
    digitalPropertyId: string,
    sessionId: string,
    request: IMobileAppCommandRequest,
  ): Observable<IMobileAppCommandResponse> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileAppSessions(sessionId)
      .command()
      .getApiUrl();

    return this.httpClient.put<IMobileAppCommandResponse>(url, request, {
      headers: {
        [ApiHeaderOption.skipLoader]: 'true',
      },
    });
  }

  // POST /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-app-sessions/:sessionId/scan
  public scan(
    workspaceId: string,
    digitalPropertyId: string,
    sessionId: string,
    request: IMobileScanRequest,
  ): Observable<IMobileAppScanResponse> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileAppSessions(sessionId)
      .scan()
      .getApiUrl();

    return this.httpClient.post<IMobileAppScanResponse>(url, request, {
      headers: {
        [ApiHeaderOption.skipLoader]: 'true',
      },
    });
  }

  // POST /workspaces/:workspaceId/digital-properties/:digitalPropertyId/mobile-app-sessions/:sessionId/token
  public generateSessionToken(
    workspaceId: string,
    digitalPropertyId: string,
    mobileAppSessionId: string,
  ): Observable<IMobileGenerateSessionTokenResponse> {
    const url: string = this.restBuilder
      .create()
      .workspaces(workspaceId)
      .digitalProperties(digitalPropertyId)
      .mobileAppSessions(mobileAppSessionId)
      .token()
      .getApiUrl();

    return this.httpClient.post<IMobileGenerateSessionTokenResponse>(url, null);
  }
}
